
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: 'What is a value-action gap?',
    options: [
      'Mismatch between values and behaviors',
      'Gap in education',
      'Difference in income',
      'Physical distance',
      'Time management issue'
    ],
    correctAnswer: 0,
    explanation: 'A value-action gap is a mismatch between values and behaviors.'
  },
  {
    question: 'How can you bring purpose to life?',
    options: [
      'Occasional inspiration',
      'Aligned routines and decisions',
      'Waiting for motivation',
      'Ignoring values',
      'Copying others'
    ],
    correctAnswer: 1,
    explanation: 'Purpose is brought to life through aligned routines and decisions.'
  },
  {
    question: 'What is a recommended way to grow?',
    options: [
      'Learn something new weekly',
      'Work overtime',
      'Travel often',
      'Buy new things',
      'Avoid challenges'
    ],
    correctAnswer: 0,
    explanation: 'Learning something new weekly is a recommended way to grow.'
  },
  {
    question: 'A meaningful life is the result of:',
    options: [
      'Consistent, aligned actions',
      'Occasional inspiration',
      'External rewards',
      'Luck',
      'Social status'
    ],
    correctAnswer: 0,
    explanation: 'A meaningful life is the result of consistent, aligned actions.'
  },
  {
    question: 'What is the purpose of a Values-to-Action Table?',
    options: [
      'Track daily actions and barriers',
      'Record expenses',
      'Plan vacations',
      'Monitor health',
      'List friends'
    ],
    correctAnswer: 0,
    explanation: 'A Values-to-Action Table helps track daily actions and barriers.'
  }
];

const LegendarialModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('legendarialM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Living with alignment</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Bridging value-action gaps</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Consistent, meaningful growth</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by acting on your values and purpose!</p>
            <p>Ready to deepen your understanding of living with alignment?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/legendarial-resources')}
          >
            Return to Legendarial Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left legendarial-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Legacy and Impact</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.<br/>
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuizQuestion.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuizQuestion.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuizQuestion.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuizQuestion.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/legendarial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegendarialModule3QuizPage;


